import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { LobbyScore } from '@sorare/core/src/atoms/icons/LobbyScore.tsx';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { LiveOrb } from '@sorare/core/src/components/LiveOrb';
import { ANY_SPORT_SCORES_MATCHES } from '@sorare/core/src/constants/__generated__/routes';
import { useConfigContext } from '@sorare/core/src/contexts/config/index.ts';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import {
  laptopAndAbove,
  tabletAndAbove,
} from '@sorare/core/src/style/mediaQuery';

import type {
  LiveScoresButtonQuery,
  LiveScoresButtonQueryVariables,
} from './__generated__/index.graphql.ts';

const LIVE_SCORES_BUTTON_QUERY = gql`
  query LiveScoresButtonQuery {
    football {
      myLiveGames {
        id
        homeGoals
        awayGoals
        minute
        statusTyped
      }
    }
  }
` as TypedDocumentNode<LiveScoresButtonQuery, LiveScoresButtonQueryVariables>;

const Root = styled(Horizontal)`
  padding: var(--unit) var(--unit) var(--unit) 0;
  justify-content: flex-end;
  @media ${tabletAndAbove} {
    padding: var(--unit) var(--double-unit) var(--unit) 0;
  }
  @media ${laptopAndAbove} {
    padding: var(--unit) var(--double-unit) var(--quadruple-unit) 0;
  }
`;

export const LiveScoresButton = () => {
  const { so5 } = useConfigContext();
  const { sport, generateSportContextPath } = useSportContext();

  const hasLiveGames = !!so5?.currentManagerHasLiveGames;
  const to = generateSportContextPath(ANY_SPORT_SCORES_MATCHES);

  const { data } = useQuery(LIVE_SCORES_BUTTON_QUERY, {
    skip: sport !== Sport.FOOTBALL,
  });

  const liveGame = data?.football.myLiveGames[0];

  return (
    <Root>
      <LiveOrb
        to={to}
        colorRgb={
          hasLiveGames ? 'var(--c-rgb-red-400)' : 'var(--c-rgb-blue-400)'
        }
        topLabel={
          hasLiveGames ? <FormattedMessage defaultMessage="Live" /> : undefined
        }
        bottomLabel={<FormattedMessage defaultMessage="Scores" />}
      >
        <LobbyScore
          size={3}
          homeGoals={liveGame?.homeGoals ?? 0}
          awayGoals={liveGame?.awayGoals ?? 0}
        />
      </LiveOrb>
    </Root>
  );
};
